/**
 * Shared FormModal field schema for the Create / Edit Project modals.
 *
 * Create and Edit both use the 6 base fields. The Hierarchical Analysis
 * option (analysis_mode select + parent_sessions multi-select) was
 * removed from the Create flow (production issue #4, 2026-07-10) — new
 * projects are always individual analyses. The backend hierarchical
 * execution plumbing is untouched, so existing hierarchical projects
 * keep working; `cleanProjectValues` still pins the payload shape the
 * backend expects (analysis_mode + parent_sessions).
 *
 * Consumers wrap each modal's onSubmit with `cleanProjectValues` to
 * scrub conditional fields (null company_type_other when not "Other").
 */

const BASE_PROJECT_FIELDS = [
  {
    name: 'analysis_name',
    type: 'text',
    label: 'Project Name',
    required: true,
    autoFocus: true,
    placeholder: 'Enter project name',
  },
  {
    name: 'company_name',
    type: 'text',
    label: 'Company Name',
    required: true,
    placeholder: 'Enter company name',
  },
  {
    name: 'company_type',
    type: 'select',
    label: 'Company Type',
    required: true,
    defaultValue: 'OEM',
    options: [
      { value: 'OEM', label: 'OEM' },
      { value: 'Tier-1', label: 'Tier-1' },
      { value: 'Tier-2', label: 'Tier-2' },
      { value: 'Other', label: 'Other' },
    ],
  },
  {
    name: 'company_type_other',
    type: 'text',
    label: 'Specify Company Type',
    placeholder: 'Specify company type',
    helpText: 'Required when Company Type is "Other".',
    visible: (allValues) => allValues.company_type === 'Other',
    validate: (value, allValues) => {
      if (allValues.company_type === 'Other' && !value?.trim()) {
        return 'Please specify the company type';
      }
      return null;
    },
  },
  {
    name: 'attack_feasibility_approach',
    type: 'select',
    label: 'Attack Feasibility Approach',
    defaultValue: 'Attack Potential',
    options: [{ value: 'Attack Potential', label: 'Attack Potential' }],
  },
  {
    name: 'tara_level',
    type: 'select',
    label: 'Project Type',
    defaultValue: 'component',
    helpText: 'Select the scope level for your TARA analysis.',
    options: [
      { value: 'feature', label: 'Feature' },
      { value: 'sub_component', label: 'Sub-Component' },
      { value: 'component', label: 'Component' },
      { value: 'subsystem', label: 'Sub-System' },
      { value: 'system', label: 'System' },
    ],
  },
];

export const PROJECT_FORM_FIELDS = BASE_PROJECT_FIELDS;

/**
 * Create Project uses the same 6 fields as Edit since the Hierarchical
 * option was removed.
 */
export const buildCreateProjectFields = () => BASE_PROJECT_FIELDS;

/**
 * Shared cleanup applied on submit — scrubs conditional fields so the
 * backend receives a stable shape:
 *  - `company_type_other` → null when Company Type isn't "Other"
 *  - `analysis_mode` pinned to 'individual' and `parent_sessions` to []
 *    (the Hierarchical option was removed from the Create flow; the
 *    backend contract still expects both keys)
 */
export const cleanProjectValues = (values) => ({
  ...values,
  company_type_other:
    values.company_type === 'Other' ? values.company_type_other : null,
  analysis_mode: 'individual',
  parent_sessions: [],
});
